/**
 * Tabblad Data: waar staat wat. L2 van blok L (Frank, 19 oktober 2026).
 *
 * Leest `/settings/dataoverzicht` en toont per stroom de plek en de opslagsoort. De
 * locatie van een aanbieder komt uit `lib/aanbieder-locatie.js` (L1), de enige tabel
 * daarvoor. Uit-schakelaars staan hier alleen waar ze al bestaan; de enige nieuwe is
 * Dropbox als opslag.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { PROVIDERS } from "@/lib/providers";
import { LOCATIE_TEKST, locatieVan } from "@/lib/aanbieder-locatie";
import DropboxSchakelaar from "./DropboxSchakelaar";

/** De Nederlandse naam van een opslagsoort; het scherm vertaalt hem met t(). PUUR. */
export const SOORT_TEKST = {
  lokaal: "Op deze Mac",
  dropbox: "Dropbox",
  nextcloud: "Nextcloud",
  s3: "S3",
  database: "Alleen in de database",
  onbekend: "Onbekend",
};

export const soortTekst = (soort) => SOORT_TEKST[soort] || SOORT_TEKST.onbekend;

function Rij({ naam, testid, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b last:border-0 py-2" data-testid={testid}>
      <span className="text-sm font-medium">{naam}</span>
      <span className="text-xs text-muted-foreground break-all">{children}</span>
    </div>
  );
}

export default function DataOverzicht() {
  const { t } = useTaal();
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/dataoverzicht`)
      .then((r) => setD(r?.data || null))
      .catch(() => setFout(true));
  }, []);

  if (fout) return <p className="text-sm text-muted-foreground">{t("Het dataoverzicht kon niet worden geladen.")}</p>;
  if (!d) return <p className="text-sm text-muted-foreground">{t("Laden...")}</p>;

  const plek = (stroom) => `${t(soortTekst(stroom?.soort))}${stroom?.plek ? ` · ${stroom.plek}` : ""}`;
  const banken = Object.entries(d.kennis?.per_soort || {});
  return (
    <div className="space-y-4" data-testid="dataoverzicht">
      <p className="text-xs text-muted-foreground">
        {t("Waar je gegevens staan, per stroom. Dit overzicht leest alleen.")}
      </p>
      <div className="border rounded-lg px-4">
        <Rij naam={t("Database")} testid="data-database">{plek(d.database)}</Rij>
        <Rij naam={t("Vault (basismap)")} testid="data-basismap">
          {d.basismap?.plek ? plek(d.basismap) : t("Nog geen basismap gekozen.")}
        </Rij>
        <Rij naam={t("Kennisbanken")} testid="data-kennis">
          {banken.length === 0
            ? t("Geen kennisbanken.")
            : banken.map(([soort, n]) => `${t(soortTekst(soort))}: ${n}`).join(" · ")}
        </Rij>
        <Rij naam={t("Vectoren")} testid="data-vectoren">{plek(d.vectoren)}</Rij>
        <Rij naam={t("Back-ups")} testid="data-backups">
          {`${d.back_ups?.aantal || 0} · ${plek(d.back_ups)}`}
        </Rij>
        <Rij naam={t("Modelaanroepen")} testid="data-modelaanroepen">
          {(d.modelaanroepen || []).length === 0
            ? t("Nog geen aanbieder gekozen.")
            : d.modelaanroepen.map((r) => (
                <span key={r.aanbieder} className="block" data-testid={`data-aanbieder-${r.aanbieder}`}>
                  {PROVIDERS[r.aanbieder]?.name || r.aanbieder} · {t(LOCATIE_TEKST[locatieVan(r.aanbieder)])}
                  {r.account ? ` · ${t("account")}` : ""}
                  {r.agents ? ` · ${r.agents} ${t("agents")}` : ""}
                </span>
              ))}
        </Rij>
      </div>
      <div className="border rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">{t("Schakelaars")}</h4>
        <DropboxSchakelaar plek="data" />
        <p className="text-xs text-muted-foreground" data-testid="data-pqc">
          {d.schakelaars?.pqc
            ? t("PQC-versleuteling staat aan.")
            : t("PQC-versleuteling staat uit.")} {t("Aan of uit zetten doe je onder Meer.")}
        </p>
        <p className="text-xs text-muted-foreground" data-testid="data-sync-uit">
          {t("Kennisbanken met synchronisatie uit")}: {d.schakelaars?.banken_sync_uit || 0}. {t("Per kennisbank te zetten bij Kennis.")}
        </p>
      </div>
    </div>
  );
}
